/* eslint-disable react-refresh/only-export-components */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U3139ContextProvider } from "./U3139Context";
import { U3139InitCreateMinuteDialogProvider } from "./U3139InitCreateMinuteDialogContext";
import { doInitLU, luHandleAppiError, normalizeU3139InitLU, type U3139InitLU } from "./U3139Service";

export const NS = "u3139";

export const U3139ROLES = {
  READ: "U3139:READ",
  ADD: "U3139:ADD",
  EDIT: "U3139:EDIT",
  DELETE: "U3139:DELETE",
  AUDITS: "U3139:AUDITS",
} as const;

export type U3139Role = (typeof U3139ROLES)[keyof typeof U3139ROLES];

const defaultInitLU: U3139InitLU = {
  launchSearch: true,
  hideSearch: false,
  nbRecordPerPage: 50,
  workNatures: [],
};

const U3139: React.FC = () => {
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const axios = useU2000AxiosAuthenticated();
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [i18nReady, setI18nReady] = useState(false);
  const [initLUReady, setInitLUReady] = useState(false);
  const [initLUData, setInitLUData] = useState<U3139InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3139"], (err) => {
      if (err) console.error("Error loading namespace u3139:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3139InitLU>({
    doInitLUFn: async () => doInitLU(axios),
    onSuccess: (data) => {
      setInitLUData(normalizeU3139InitLU(data));
      setInitLUReady(true);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    if (!i18nReady) return;
    handleInitLU.getInitLU();
  }, [i18nReady, t]);

  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3139ContextProvider initLU={initLUData}>
      <U3139InitCreateMinuteDialogProvider>
        <Outlet />
      </U3139InitCreateMinuteDialogProvider>
    </U3139ContextProvider>
  );
};

export default U3139;
